import { useState } from 'react';
import { hasStablecoinRails } from '@incpt/shared';
import { UsdcIcon } from '../icons/UsdcIcon';
import { UsdtIcon } from '../icons/UsdtIcon';
import { addressDisplayName, shortenAddress } from '../../lib/walletNames';
import type { Balance, Wallet } from '../../hooks/useWallet';
import type { Chain, StableToken } from '../../lib/transfers';

/** Card pay/receive flows are STABLECOIN-ONLY: native ETH/BTC must never fund
 *  or receive card money (the server also rejects them — payment_wrong_token). */
const STABLE_TOKENS: readonly string[] = ['USDT', 'USDC'];

export interface PaySource {
  key: string;
  token: StableToken;
  chain: Chain;
  balance: number;
  fromIndex: number;
  /** WalletAddress.id — must be sent to POST /transfers so refee targets the right account. */
  addressId: string;
  /** Real per-address name (custom label or "Wallet N"). */
  name: string;
  /** Truncated address (6 + … + 4), same format as the coin-detail list. */
  addressShort: string;
  /** Full address funds are held at / sent to (smart account for eth, else EOA). */
  address: string;
}

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));
const chainName = (c: Chain) => (c === 'eth' ? 'Ethereum' : c === 'btc' ? 'Bitcoin' : 'Tron');

type Addr = Wallet['addresses'][number];
const amountOf = (b: Balance) => Number(b.raw) / 10 ** b.decimals;
const matchAddr = (a: Addr, b: Balance) =>
  a.chain === b.chain && (a.eoaAddress === b.address || a.smartAccountAddress === b.address);

function makeSource(a: Addr, token: StableToken, balance: number, walletWord: string): PaySource {
  return {
    key: `${token}:${a.chain}:${a.eoaAddress}`,
    token,
    chain: a.chain,
    balance,
    fromIndex: a.derivationIndex,
    addressId: a.id,
    name: addressDisplayName(a, walletWord),
    addressShort: shortenAddress(a.eoaAddress),
    address: a.chain === 'eth' ? (a.smartAccountAddress ?? a.eoaAddress) : a.eoaAddress,
  };
}

/** Pay-from sources: one per funded address × token, real name + truncated address.
 *  When nothing is funded, still lists every wallet at $0 so one can be picked. */
export function buildPaySources(
  wallet: Wallet | null,
  balances: Balance[],
  walletWord: string,
): PaySource[] {
  if (!wallet) return [];
  const funded = balances
    // Natives (ETH/BTC rows now present in balances) are NOT payable sources.
    .filter((b) => STABLE_TOKENS.includes(b.token))
    .map((b) => {
      const a = wallet.addresses.find((x) => matchAddr(x, b));
      return a ? makeSource(a, b.token as StableToken, amountOf(b), walletWord) : null;
    })
    .filter((s): s is PaySource => !!s && s.balance > 0);
  if (funded.length) return funded;
  // Zero-balance fallback: stablecoin-capable addresses only.
  return wallet.addresses
    .filter((a) => hasStablecoinRails(a.chain))
    .map((a) => makeSource(a, 'USDT', 0, walletWord));
}

/** Default pay-from wallet: first USDT TRC-20 by derivation index, else the first source. */
export function pickDefaultPaySource(sources: PaySource[]): PaySource | null {
  const usdtTrx = sources
    .filter((s) => s.token === 'USDT' && s.chain === 'trx')
    .sort((a, b) => a.fromIndex - b.fromIndex);
  return usdtTrx[0] ?? sources[0] ?? null;
}

/** Prefer the funded USDT TRC-20 address with the largest balance (presale / pay flows). */
export function pickRichestPaySource(sources: PaySource[]): PaySource | null {
  const usdtTrx = sources.filter((s) => s.token === 'USDT' && s.chain === 'trx');
  const pool = usdtTrx.length > 0 ? usdtTrx : sources;
  if (pool.length === 0) return null;
  return pool.reduce((best, s) => (s.balance > best.balance ? s : best));
}

/** Receive/destination sources: one per wallet address for a fixed token (every
 *  address, regardless of balance) — used to pick where withdrawn funds land. */
export function buildReceiveSources(
  wallet: Wallet | null,
  balances: Balance[],
  walletWord: string,
  token: StableToken = 'USDT',
): PaySource[] {
  if (!wallet) return [];
  return wallet.addresses
    // Stablecoin destinations only — a coin-only chain can't receive USDT.
    .filter((a) => hasStablecoinRails(a.chain))
    .map((a) => {
      const b = balances.find((x) => x.token === token && matchAddr(a, x));
      return makeSource(a, token, b ? amountOf(b) : 0, walletWord);
    });
}

function TokenLogo({ token, size = 28 }: { token: StableToken; size?: number }) {
  return token === 'USDC' ? <UsdcIcon size={size} /> : <UsdtIcon size={size} />;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className={`shrink-0 text-muted transition-transform ${open ? 'rotate-180' : ''}`}
    >
      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SourceInfo({ s }: { s: PaySource }) {
  return (
    <span className="flex min-w-0 items-center gap-3">
      <TokenLogo token={s.token} />
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-[15px] font-medium text-ink">
          {s.name || `${s.token} · ${chainName(s.chain)}`}
        </span>
        <span className="truncate text-[12px] text-muted">
          {s.token} · {chainName(s.chain)} · {s.addressShort}
        </span>
      </span>
    </span>
  );
}

function BalanceTag({ s, required }: { s: PaySource; required: number }) {
  const enough = s.balance >= required;
  return (
    <span className={`shrink-0 text-[13px] font-medium ${enough ? 'text-muted' : 'text-[#ff3b30]'}`}>
      ${fmt(s.balance)}
    </span>
  );
}

/** "Pay from" picker: a collapsed dropdown (scales to many wallets) listing every
 *  stablecoin source highest-balance-first, each with its token icon + real name. */
export function SourceSelect({
  sources,
  value,
  onChange,
  required = 0,
}: {
  sources: PaySource[];
  value: string | null;
  onChange: (key: string) => void;
  required?: number;
}) {
  const [open, setOpen] = useState(false);
  const sorted = [...sources].sort((a, b) => b.balance - a.balance);
  const sel =
    sources.find((s) => s.key === value) ?? pickDefaultPaySource(sources) ?? sorted[0] ?? null;
  if (!sel) return null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 rounded-[16px] border border-black/10 bg-white px-3.5 py-3 text-left"
      >
        <SourceInfo s={sel} />
        <span className="flex shrink-0 items-center gap-2">
          <BalanceTag s={sel} required={required} />
          <Chevron open={open} />
        </span>
      </button>

      {open && (
        <div className="mt-2 flex max-h-60 flex-col gap-2 overflow-y-auto rounded-[18px] border border-black/10 bg-[#f6f7f8] p-2">
          {sorted.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => {
                onChange(s.key);
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between gap-3 rounded-[14px] border px-3 py-2.5 text-left ${
                s.key === sel.key ? 'border-[#377dff] bg-[#377dff]/[0.06]' : 'border-transparent bg-white'
              }`}
            >
              <SourceInfo s={s} />
              <BalanceTag s={s} required={required} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
